import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  Edit,
  Trash2,
  UserCheck,
  RotateCcw,
  CheckCircle,
  Clock,
  AlertCircle,
  Upload,
  FileText,
  Image,
  Download,
  Eye,
  QrCode,
  ExternalLink,
  ChevronRight,
  ChevronLeft as ChevLeft,
  X,
  Plus,
} from "lucide-react";
import {
  useAsset,
  useActivityLog,
  useEditLog,
  useReassignAsset,
  useReturnAsset,
  useLogCheck,
  useArchiveAsset,
  useSystemConfig,
} from "../../hooks/useAssets";
import {
  DATA_ISSUE_LABELS,
  MISSING_FIELD_LABELS,
  OWNERSHIP_CLASS_LABELS,
} from "../../utils/constants";
import { useAuth } from "../../context/AuthContext";
import {
  Button,
  Badge,
  Modal,
  Input,
  Spinner,
} from "../../components/ui/index.jsx";
import {
  StatusBadge,
  getEventLabel,
} from "../../components/asset/AssetHelpers.jsx";
import {
  formatDate,
  formatRelative,
  formatIDR,
  formatBytes,
} from "../../utils/format";
import apiClient from "../../api/client";
import { HolderSelector } from "../../components/HolderSelector";
import { StorageContentsTab } from "../../components/asset/StorageContentsTab";
import { FileIndexTab } from "../../components/asset/FileIndexTab";
import { QrPreviewButton } from "../../components/asset/QrLabelPreview";

// ---------------------------------------------------------------------------
// File upload
// ---------------------------------------------------------------------------
function useFileUpload(assetId, onDone) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  async function upload(files, documentType = "OTHER", description = null) {
    // Accept a single File or an array of Files
    const fileList = Array.isArray(files) ? files : [files];
    setUploading(true);
    setError(null);
    try {
      // Upload sequentially — keeps server load low, shows progress clearly
      for (const file of fileList) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("assetId", assetId);
        formData.append("documentType", documentType);
        if (description) formData.append("notes", description);
        await apiClient.post("/api/documents/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onDone?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return { upload, uploading, error };
}

// ---------------------------------------------------------------------------
// Delete document
// ---------------------------------------------------------------------------
async function deleteDoc(docId) {
  await apiClient.delete(`/api/documents/${docId}`);
}

// ---------------------------------------------------------------------------
// Upload button
// ---------------------------------------------------------------------------
function UploadButton({
  assetId,
  accept,
  documentType,
  label,
  icon: Icon,
  onDone,
  description,
  multiple = false,
}) {
  const inputRef = useRef();
  const { upload, uploading, error } = useFileUpload(assetId, onDone);

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) upload(files, documentType, description);
          e.target.value = "";
        }}
      />
      <Button
        variant="secondary"
        size="sm"
        isLoading={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {!uploading && <Icon className="h-4 w-4" />}
        {label}
      </Button>
      {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document preview modal
// ---------------------------------------------------------------------------
function DocumentPreviewModal({ doc, isOpen, onClose }) {
  // The file is fetched through the authenticated API (the backend streams it
  // from R2) and shown from a blob: URL. Two things rule out the alternatives:
  //  - the public R2 domain blocks /documents/* at Cloudflare, so a public URL
  //    can't be used for anything but photos;
  //  - a plain <a href="/api/...?token="> is relative, so in production it
  //    resolves on the Pages origin (SPA fallback), not the backend.
  // apiClient has the right baseURL and sends the token as a header.
  const [fileUrl, setFileUrl] = useState(null);
  const [blobType, setBlobType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !doc) return;
    let cancelled = false;
    let objectUrl = null;
    setFileUrl(null);
    setError(null);
    setLoading(true);
    apiClient
      .get(`/api/documents/${doc.id}/proxy`, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setBlobType(data.type || "");
        setFileUrl(objectUrl);
      })
      .catch(async (err) => {
        if (cancelled) return;
        // With responseType "blob" an error body arrives as a Blob too
        let message = "Failed to load file";
        try {
          const body = err.response?.data;
          const parsed = body instanceof Blob ? JSON.parse(await body.text()) : body;
          message = parsed?.error || message;
        } catch {
          /* keep the generic message */
        }
        setError(message);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, doc?.id]);

  if (!isOpen) return null;

  const mimeType = doc?.mimeType || blobType || "";
  const isImage = mimeType.startsWith("image/");
  const isPdf = mimeType === "application/pdf";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={doc.fileName} size="xl">
      <div className="min-h-64">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        )}
        {error && (
          <p className="text-sm text-rose-600 text-center py-12">{error}</p>
        )}
        {fileUrl && isImage && (
          <img
            src={fileUrl}
            alt={doc.fileName}
            className="max-w-full rounded-lg mx-auto"
          />
        )}
        {fileUrl && isPdf && (
          <iframe
            src={fileUrl}
            title={doc.fileName}
            className="w-full rounded-lg border border-slate-200"
            style={{ height: "70vh" }}
          />
        )}
        {fileUrl && !isImage && !isPdf && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              Preview not available for this file type.
            </p>
          </div>
        )}
        {fileUrl && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-slate-100">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:underline"
            >
              <ExternalLink className="h-4 w-4" /> Open in new tab
            </a>
            <a
              href={fileUrl}
              download={doc.fileName}
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
            >
              <Download className="h-4 w-4" /> Download
            </a>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Photo gallery — horizontal scroll with prev/next navigation
// ---------------------------------------------------------------------------
function PhotoGallery({ photos, canEdit, onPreview, onDelete }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (current >= photos.length && photos.length > 0)
      setCurrent(photos.length - 1);
  }, [photos.length]);

  if (photos.length === 0) return null;

  const photo = photos[current];

  return (
    <div className="space-y-2">
      {/* Main photo */}
      <div
        className="relative group rounded-lg overflow-hidden bg-slate-100"
        style={{ aspectRatio: "16/9" }}
      >
        <img
          src={photo.fileUrl}
          alt={photo.fileName}
          className="w-full h-full object-contain cursor-pointer"
          onClick={() => onPreview(photo)}
        />
        {/* Overlay controls */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onPreview(photo)}
            className="bg-white rounded-full p-1.5 shadow hover:bg-slate-50"
          >
            <Eye className="h-3.5 w-3.5 text-slate-600" />
          </button>
          {canEdit && (
            <button
              onClick={() => onDelete(photo)}
              className="bg-white rounded-full p-1.5 shadow hover:bg-rose-50"
            >
              <Trash2 className="h-3.5 w-3.5 text-rose-500" />
            </button>
          )}
        </div>
        {/* Prev/Next */}
        {photos.length > 1 && (
          <>
            <button
              onClick={() =>
                setCurrent((c) => (c - 1 + photos.length) % photos.length)
              }
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1 shadow transition-colors"
            >
              <ChevLeft className="h-4 w-4 text-slate-700" />
            </button>
            <button
              onClick={() => setCurrent((c) => (c + 1) % photos.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1 shadow transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-slate-700" />
            </button>
          </>
        )}
        {/* Counter */}
        {photos.length > 1 && (
          <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
            {current + 1} / {photos.length}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {photos.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {photos.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setCurrent(i)}
              className={`shrink-0 rounded overflow-hidden border-2 transition-colors ${
                i === current ? "border-primary-500" : "border-transparent"
              }`}
              style={{ width: 52, height: 40 }}
            >
              <img
                src={p.fileUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoice section — single file, inline. Also used for the installment form,
// which is the same shape: one attached document of one type.
// ---------------------------------------------------------------------------
function InvoiceSection({
  invoiceDocs,
  canEdit,
  assetId,
  onPreview,
  onDelete,
  onUploaded,
  documentType = "INVOICE",
  label = "Invoice Document",
  emptyText = "No invoice attached.",
}) {
  const invoice = invoiceDocs[0] ?? null;

  return (
    <div className="mt-4 pt-4 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-slate-500 min-w-40 shrink-0">
          {label}
        </span>
        {canEdit && !invoice && (
          <UploadButton
            assetId={assetId}
            accept="application/pdf,image/*"
            documentType={documentType}
            label="Attach"
            icon={FileText}
            onDone={onUploaded}
          />
        )}
      </div>
      {invoice ? (
        <div className="flex items-center gap-3">
          <FileText className="h-4 w-4 text-slate-400 shrink-0" />
          <button
            onClick={() => onPreview(invoice)}
            className="text-sm text-primary-600 hover:underline truncate"
          >
            {invoice.fileName}
          </button>
          {canEdit && (
            <button
              onClick={() => onDelete(invoice)}
              className="ml-auto text-slate-300 hover:text-rose-500 transition-colors shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic">{emptyText}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Supporting documents — multiple, with description tag
// ---------------------------------------------------------------------------
function SupportingDocuments({
  docs,
  canEdit,
  assetId,
  onPreview,
  onDelete,
  onUploaded,
}) {
  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState("");
  const inputRef = useRef();
  const { upload, uploading } = useFileUpload(assetId, () => {
    onUploaded();
    setShowForm(false);
    setDescription("");
  });

  function handleFileChosen(e) {
    const files = Array.from(e.target.files || []);
    if (files.length) upload(files, "OTHER", description);
    e.target.value = "";
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Supporting Documents
        </h2>
        {canEdit && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-4 w-4" /> Add
          </Button>
        )}
      </div>

      {/* Upload form */}
      {showForm && (
        <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
          <Input
            label="Document title / tag"
            placeholder="e.g. Surat Persetujuan, BAST, Manual Book..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChosen}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              isLoading={uploading}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-4 w-4" /> Choose File
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setShowForm(false);
                setDescription("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Document list */}
      {docs.length === 0 && !showForm ? (
        <p className="text-sm text-slate-400 italic">
          No supporting documents attached.
        </p>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 group transition-colors"
            >
              <FileText className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => onPreview(doc)}
                  className="text-sm text-primary-600 hover:underline truncate block text-left"
                >
                  {doc.fileName}
                </button>
                {doc.description && (
                  <span className="inline-block mt-0.5 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                    {doc.description}
                  </span>
                )}
              </div>
              {canEdit && (
                <button
                  onClick={() => onDelete(doc)}
                  className="text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Warranty document section
// ---------------------------------------------------------------------------
function WarrantyDocSection({
  docs,
  canEdit,
  assetId,
  onPreview,
  onDelete,
  onUploaded,
}) {
  const doc = docs[0] ?? null;
  return (
    <div className="mt-4 pt-4 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Warranty Document
        </span>
        {canEdit && !doc && (
          <UploadButton
            assetId={assetId}
            accept="application/pdf,image/*"
            documentType="WARRANTY"
            label="Attach"
            icon={FileText}
            onDone={onUploaded}
          />
        )}
      </div>
      {doc ? (
        <div className="flex items-center gap-3">
          <FileText className="h-4 w-4 text-slate-400 shrink-0" />
          <button
            onClick={() => onPreview(doc)}
            className="text-sm text-primary-600 hover:underline truncate"
          >
            {doc.fileName}
          </button>
          {canEdit && (
            <button
              onClick={() => onDelete(doc)}
              className="ml-auto text-slate-300 hover:text-rose-500 transition-colors shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic">
          No warranty document attached.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// QR placeholder
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Action modals
// ---------------------------------------------------------------------------
function ReassignModal({ assetId, isOpen, onClose }) {
  const [holderId, setHolderId] = useState(null);
  const [notes, setNotes] = useState("");
  const mutation = useReassignAsset(assetId);

  function handleClose() {
    onClose();
    setHolderId(null);
    setNotes("");
    mutation.reset?.();
  }

  async function handle() {
    if (!holderId) return;
    await mutation.mutateAsync({ toHolderId: holderId, notes });
    handleClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Reassign Asset">
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-1">
            New Holder
          </label>
          <HolderSelector
            value={holderId}
            onChange={setHolderId}
            placeholder="Search by name or email..."
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-1">
            Notes (optional)
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            placeholder="Reason for reassignment..."
          />
        </div>
        {mutation.error && (
          <p className="text-sm text-rose-600">
            {mutation.error.response?.data?.error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handle}
            isLoading={mutation.isPending}
            disabled={!holderId}
          >
            Reassign
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ReturnModal({ assetId, isOpen, onClose }) {
  const [notes, setNotes] = useState("");
  const mutation = useReturnAsset(assetId);
  async function handle() {
    await mutation.mutateAsync({ notes });
    onClose();
    setNotes("");
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Return Asset">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Clears the holder and sets the condition to <strong>Idle</strong>.
        </p>
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-1">
            Notes (optional)
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>
        {mutation.error && (
          <p className="text-sm text-rose-600">
            {mutation.error.response?.data?.error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handle}
            isLoading={mutation.isPending}
          >
            Return Asset
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function LogCheckModal({ assetId, isOpen, onClose }) {
  const [condition, setCondition] = useState("Good");
  const [notes, setNotes] = useState("");
  const mutation = useLogCheck(assetId);
  async function handle() {
    await mutation.mutateAsync({ condition, notes });
    onClose();
    setNotes("");
  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Log Inspection">
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-1">
            Condition
          </label>
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option>Good</option>
            <option>Issue</option>
            <option>Damaged</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-1">
            Notes
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handle} isLoading={mutation.isPending}>
            Save Check
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Drive capacity & usage — real numbers from the scan script (df/Get-PSDrive
// at scan time), not a manually-typed estimate. Falls back to the legacy
// free-text storageCapacity field if no scan has reported real numbers yet.
// ---------------------------------------------------------------------------
function DriveCapacitySummary({ asset }) {
  const { driveCapacityBytes, driveFreeBytesAtScan, storageCapacity } = asset;

  if (driveCapacityBytes == null || driveFreeBytesAtScan == null) {
    if (!storageCapacity) return null;
    return (
      <div className="bg-slate-50 rounded-lg p-4 mb-4">
        <p className="text-xs text-slate-500">
          Capacity: <span className="font-medium">{storageCapacity}</span>{" "}
          <span className="text-slate-400">
            (manually entered — upload a File Index scan for real usage)
          </span>
        </p>
      </div>
    );
  }

  const usedBytes = Math.max(0, driveCapacityBytes - driveFreeBytesAtScan);
  const pct = Math.min(
    100,
    Math.round((usedBytes / driveCapacityBytes) * 100),
  );

  return (
    <div className="bg-slate-50 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
        <span>Storage usage</span>
        <span>
          {formatBytes(usedBytes)} used · {formatBytes(driveFreeBytesAtScan)}{" "}
          free · {formatBytes(driveCapacityBytes)} total
        </span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct > 90 ? "bg-rose-500" : pct > 70 ? "bg-amber-500" : "bg-primary-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-slate-400 mt-1">
        {pct}% used — as of last scan
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Storage Unit tags — rollup of every tag used across this unit's Contents
// entries (kept in sync server-side on every content write). Read-only
// here; tags are still added/removed per Contents entry.
// ---------------------------------------------------------------------------
function StorageTags({ tags }) {
  if (!tags?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-4">
      <span className="text-xs text-slate-400 mr-1">Tags on this drive:</span>
      {tags.map((t) => (
        <span
          key={t}
          className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"
        >
          #{t}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Info row
// ---------------------------------------------------------------------------
function InfoRow({ label, value, mono }) {
  return (
    <div className="flex gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500 min-w-40 shrink-0">{label}</span>
      <span className={`text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>
        {value ?? <span className="text-slate-300">—</span>}
      </span>
    </div>
  );
}

function ActivityLog({ assetId }) {
  const { data: logs = [], isLoading } = useActivityLog(assetId);
  if (isLoading)
    return (
      <div className="py-8 flex justify-center">
        <Spinner />
      </div>
    );
  if (!logs.length)
    return (
      <p className="text-sm text-slate-400 py-8 text-center">
        No activity logged yet.
      </p>
    );
  return (
    <div className="space-y-1">
      {logs.map((log) => (
        <div
          key={log.id}
          className="flex gap-4 py-3 border-b border-slate-100 last:border-0"
        >
          <div className="mt-0.5 h-6 w-6 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
            <Clock className="h-3 w-3 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-800">
                {getEventLabel(log.eventType)}
              </span>
              {log.context?.condition && (
                <Badge
                  variant={
                    log.context.condition === "Good"
                      ? "success"
                      : log.context.condition === "Damaged"
                        ? "danger"
                        : "warning"
                  }
                >
                  {log.context.condition}
                </Badge>
              )}
            </div>
            {log.notes && (
              <p className="text-sm text-slate-500 mt-0.5">{log.notes}</p>
            )}
            <p className="text-xs text-slate-400 mt-1">
              {log.performer?.name} · {formatRelative(log.occurredAt)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function EditLog({ assetId }) {
  const { data: logs = [], isLoading } = useEditLog(assetId, true);
  if (isLoading)
    return (
      <div className="py-8 flex justify-center">
        <Spinner />
      </div>
    );
  if (!logs.length)
    return (
      <p className="text-sm text-slate-400 py-8 text-center">
        No edits recorded.
      </p>
    );
  return (
    <div className="space-y-3">
      {logs.map((log) => (
        <div key={log.id} className="border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              {log.performer?.name}
            </span>
            <span className="text-xs text-slate-400">
              {formatRelative(log.occurredAt)}
            </span>
          </div>
          <div className="space-y-1">
            {Object.entries(log.changes).map(([field, { from, to }]) => (
              <div key={field} className="text-xs text-slate-600 flex gap-2">
                <span className="font-medium w-36 shrink-0">{field}</span>
                <span className="text-rose-500 line-through truncate">
                  {String(from ?? "—")}
                </span>
                <span className="text-slate-400">→</span>
                <span className="text-emerald-600 truncate">
                  {String(to ?? "—")}
                </span>
              </div>
            ))}
          </div>
          {log.notes && (
            <p className="text-xs text-slate-400 mt-2 italic">{log.notes}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const TABS = ["Details", "Activity", "Edit Log"];

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Back goes to wherever the user came from — the list with its filters, the
  // dashboard, etc. `from` is set by the list; otherwise use browser history,
  // and only fall back to the plain list when the page was opened directly.
  const from = location.state?.from;
  function goBack(opts) {
    if (from) navigate(from, opts);
    else if (location.key !== "default" && !opts) navigate(-1);
    else navigate("/assets", opts);
  }
  const { user } = useAuth();

  const { data: asset, isLoading, isError, refetch } = useAsset(id);
  const { data: config } = useSystemConfig();
  const archiveMutation = useArchiveAsset();

  const [tab, setTab] = useState("Details");
  const [modal, setModal] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const [deletingId, setDeletingId] = useState(null);

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  if (isError || !asset)
    return (
      <div className="text-center py-20">
        <AlertCircle className="h-10 w-10 text-rose-300 mx-auto mb-3" />
        <p className="text-slate-500">Asset not found or access denied.</p>
        <Button
          variant="secondary"
          className="mt-4"
          onClick={() => goBack()}
        >
          Back
        </Button>
      </div>
    );

  if (asset._restrictedMessage)
    return (
      <div className="text-center py-20">
        <AlertCircle className="h-10 w-10 text-amber-300 mx-auto mb-3" />
        <p className="text-slate-700 font-medium">Restricted Asset</p>
        <p className="text-slate-500 text-sm mt-1">
          {asset._restrictedMessage}
        </p>
      </div>
    );

  const cap = asset._capabilities ?? {};
  const isStorageUnit = asset.assetType === "STORAGE_UNIT";
  const tabs = [...TABS, ...(isStorageUnit ? ["Contents"] : [])].filter(
    (t) => t !== "Edit Log" || cap.canViewEditLog,
  );

  async function handleArchive() {
    if (!confirm(`Archive ${asset.assetNumber}? Can be restored by Admin.`))
      return;
    await archiveMutation.mutateAsync({ id });
    goBack({ replace: true });
  }

  async function handleDeleteDoc(doc) {
    if (!confirm(`Delete "${doc.fileName}"?`)) return;
    setDeletingId(doc.id);
    try {
      await deleteDoc(doc.id);
      refetch();
    } finally {
      setDeletingId(null);
    }
  }

  const allDocs = asset.documents ?? [];
  const photos = allDocs.filter((d) => d.documentType === "PHOTO");
  const invoiceDocs = allDocs.filter((d) => d.documentType === "INVOICE");
  const installmentDocs = allDocs.filter(
    (d) => d.documentType === "INSTALLMENT_FORM",
  );
  const warrantyDocs = allDocs.filter((d) => d.documentType === "WARRANTY");
  const supportingDocs = allDocs.filter((d) => d.documentType === "OTHER");

  const dep = asset.depreciation;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => goBack()}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900">
                {asset.name}
              </h1>
              <StatusBadge status={asset.status} />
              {asset.isRestricted && (
                <Badge variant="warning">Restricted</Badge>
              )}
            </div>
            <p className="font-mono text-sm text-slate-500 mt-0.5 flex items-center gap-2">
              {asset.assetNumber}
              {config && !config.assetNumbersFrozen && (
                <span
                  title="Asset numbers are still provisional: this number can change if the entity, category or entity acquisition date is corrected, or another asset in the same group is added or moved. It becomes permanent when numbers are frozen."
                  className="font-sans"
                >
                  <Badge variant="warning">Provisional</Badge>
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          {cap.canLogCheck && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setModal("check")}
            >
              <CheckCircle className="h-4 w-4" /> Check
            </Button>
          )}
          {cap.canReturn && asset.holderId && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setModal("return")}
            >
              <RotateCcw className="h-4 w-4" /> Return
            </Button>
          )}
          {cap.canReassign && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setModal("reassign")}
            >
              <UserCheck className="h-4 w-4" /> Reassign
            </Button>
          )}
          {cap.canEdit && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/assets/${id}/edit`, { state: location.state })}
            >
              <Edit className="h-4 w-4" /> Edit
            </Button>
          )}
          {cap.canDelete && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleArchive}
              isLoading={archiveMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Details tab */}
      {tab === "Details" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Left column */}
            <div className="lg:col-span-2 space-y-5">
              {/* Asset Info */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Asset Info
                </h2>
                <InfoRow label="Row No." value={asset.rowNumber} mono />
                <InfoRow label="Asset Number" value={asset.assetNumber} mono />
                <InfoRow
                  label="Asset Type"
                  value={
                    asset.assetType === "STORAGE_UNIT"
                      ? "Storage Unit"
                      : "Standard"
                  }
                />
                <InfoRow
                  label="Entity"
                  value={
                    asset.entity
                      ? `${asset.entity.code} — ${asset.entity.name}`
                      : null
                  }
                />
                <InfoRow label="Category" value={asset.category?.name} />
                <InfoRow
                  label="Brand / Model"
                  value={
                    [asset.brand, asset.model].filter(Boolean).join(" · ") ||
                    null
                  }
                />
                <InfoRow
                  label="Serial Number"
                  value={asset.serialNumber}
                  mono
                />
                <InfoRow label="Location" value={asset.location} />
                <InfoRow label="Description" value={asset.description} />
              </div>

              {/* Data status — only present for roles that see financial data */}
              {(asset.dataIssues?.length > 0 ||
                asset.missingFields?.length > 0) && (
                <div
                  className={`border rounded-xl p-5 ${
                    asset.dataIssues?.length > 0
                      ? "bg-amber-50 border-amber-200"
                      : "bg-white border-slate-200"
                  }`}
                >
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Data Status
                  </h2>
                  {asset.dataIssues?.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {asset.dataIssues.map((issue) => (
                        <Badge key={issue} variant="warning">
                          {DATA_ISSUE_LABELS[issue] ?? issue}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 mb-2">
                      No issue flags set.
                    </p>
                  )}
                  {asset.dataNotes && (
                    <p className="text-sm text-slate-700 whitespace-pre-wrap mb-2">
                      {asset.dataNotes}
                    </p>
                  )}
                  {asset.missingFields?.length > 0 && (
                    <p className="text-xs text-slate-500">
                      Still empty:{" "}
                      {asset.missingFields
                        .map((f) => MISSING_FIELD_LABELS[f] ?? f)
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}

              {/* Acquisition — both pairs; the fields are absent for VIEWER */}
              {asset.entityPurchaseDate !== undefined && (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Acquisition
                  </h2>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mt-1 mb-1">
                    Original — first vendor purchase
                  </p>
                  <InfoRow
                    label="Purchase Date"
                    value={
                      asset.purchaseDate ? formatDate(asset.purchaseDate) : null
                    }
                  />
                  <InfoRow
                    label="Purchase Price"
                    value={
                      asset.purchasePrice != null
                        ? formatIDR(asset.purchasePrice)
                        : null
                    }
                  />
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mt-4 mb-1">
                    Entity — current entity{" "}
                    {asset.entity ? `(${asset.entity.code})` : ""}
                  </p>
                  <InfoRow
                    label="Acquisition Date"
                    value={
                      asset.entityPurchaseDate
                        ? formatDate(asset.entityPurchaseDate)
                        : null
                    }
                  />
                  <InfoRow
                    label="Acquisition Price"
                    value={
                      asset.entityPurchasePrice != null
                        ? formatIDR(asset.entityPurchasePrice)
                        : null
                    }
                  />
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mt-4 mb-1">
                    Purchase details
                  </p>
                  <InfoRow label="Payment Source" value={asset.paymentSource} />
                  <InfoRow label="Vendor" value={asset.vendor} />
                  <InfoRow
                    label="Invoice Ref."
                    value={asset.invoiceNumber}
                    mono
                  />
                  <InvoiceSection
                    invoiceDocs={invoiceDocs}
                    canEdit={cap.canEdit}
                    assetId={id}
                    onPreview={setPreviewDoc}
                    onDelete={handleDeleteDoc}
                    onUploaded={refetch}
                  />
                </div>
              )}

              {/* Ownership — fields are absent for VIEWER */}
              {asset.ownership !== undefined && (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Ownership
                  </h2>
                  <InfoRow
                    label="Class"
                    value={OWNERSHIP_CLASS_LABELS[asset.ownershipClass]}
                  />
                  {asset.ownership === "OWNED" && (
                    <>
                      <InfoRow
                        label="Installment Period"
                        value={
                          asset.installmentStartDate || asset.installmentEndDate
                            ? `${asset.installmentStartDate ? formatDate(asset.installmentStartDate) : "—"} – ${asset.installmentEndDate ? formatDate(asset.installmentEndDate) : "—"}`
                            : null
                        }
                      />
                      <InfoRow
                        label="Monthly Installment"
                        value={
                          asset.installmentMonthlyAmount != null
                            ? formatIDR(asset.installmentMonthlyAmount)
                            : null
                        }
                      />
                      {(installmentDocs.length > 0 ||
                        asset.installmentEndDate ||
                        cap.canEdit) && (
                        <InvoiceSection
                          invoiceDocs={installmentDocs}
                          canEdit={cap.canEdit}
                          assetId={id}
                          onPreview={setPreviewDoc}
                          onDelete={handleDeleteDoc}
                          onUploaded={refetch}
                          documentType="INSTALLMENT_FORM"
                          label="Installment Form"
                          emptyText="No installment form attached."
                        />
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Depreciation */}
              {dep && (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Depreciation (Straight-Line)
                  </h2>
                  <InfoRow
                    label="Entity Acquisition Price"
                    value={formatIDR(asset.entityPurchasePrice)}
                  />
                  <InfoRow
                    label="Book Value"
                    value={formatIDR(dep.bookValue)}
                  />
                  <InfoRow
                    label="Accumulated Depr."
                    value={formatIDR(dep.accumulatedDepreciation)}
                  />
                  <InfoRow
                    label="Yearly Depr."
                    value={formatIDR(dep.yearlyDepreciation)}
                  />
                  <InfoRow
                    label="Useful Life"
                    value={`${dep.usefulLifeYears} years (${asset.usefulLifeMonths} months)`}
                  />
                  <InfoRow
                    label="Time Elapsed"
                    value={`${dep.yearsElapsed?.toFixed(1)} yrs elapsed — ${dep.yearsRemaining?.toFixed(1)} yrs remaining`}
                  />
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                      <span>Depreciated</span>
                      <span>
                        {asset.entityPurchasePrice
                          ? Math.round(
                              (dep.accumulatedDepreciation /
                                asset.entityPurchasePrice) *
                                100,
                            )
                          : 0}
                        %
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${dep.fullyDepreciated ? "bg-slate-400" : "bg-primary-500"}`}
                        style={{
                          width: `${asset.entityPurchasePrice ? Math.min(100, Math.round((dep.accumulatedDepreciation / asset.entityPurchasePrice) * 100)) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  {dep.fullyDepreciated && (
                    <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                      ⚠ Fully depreciated
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right column */}
            <div className="space-y-5">
              {/* Holder */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Holder
                </h2>
                {asset.holder ? (
                  <div>
                    <p className="font-medium text-slate-900">
                      {asset.holder.name}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {asset.holder.email}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Unassigned</p>
                )}
              </div>

              {/* Photos */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Photos
                  </h2>
                  {cap.canEdit && (
                    <UploadButton
                      assetId={id}
                      accept="image/*"
                      documentType="PHOTO"
                      label="Add Photos"
                      icon={Image}
                      onDone={refetch}
                      multiple={true}
                    />
                  )}
                </div>
                {photos.length > 0 ? (
                  <PhotoGallery
                    photos={photos}
                    canEdit={cap.canEdit}
                    onPreview={setPreviewDoc}
                    onDelete={handleDeleteDoc}
                  />
                ) : (
                  <p className="text-sm text-slate-400 italic">
                    No photos yet.
                  </p>
                )}
              </div>

              {/* Warranty */}
              {(asset.warrantyEndDate ||
                warrantyDocs.length > 0 ||
                cap.canEdit) && (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Warranty
                  </h2>
                  <InfoRow label="Provider" value={asset.warrantyProvider} />
                  <InfoRow
                    label="Start"
                    value={formatDate(asset.warrantyStartDate)}
                  />
                  <InfoRow
                    label="End"
                    value={formatDate(asset.warrantyEndDate)}
                  />
                  {asset.warrantyStatus && (
                    <div
                      className={`mt-3 rounded-lg px-3 py-2 text-xs font-medium ${
                        asset.warrantyStatus.status === "ACTIVE"
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : asset.warrantyStatus.status === "EXPIRING_SOON"
                            ? "bg-amber-50 text-amber-700 border border-amber-200"
                            : "bg-rose-50 text-rose-700 border border-rose-200"
                      }`}
                    >
                      {asset.warrantyStatus.status === "ACTIVE" &&
                        `✓ Active — ${asset.warrantyStatus.daysRemaining} days remaining`}
                      {asset.warrantyStatus.status === "EXPIRING_SOON" &&
                        `⚠ Expiring in ${asset.warrantyStatus.daysRemaining} days`}
                      {asset.warrantyStatus.status === "EXPIRED" &&
                        `✕ Expired ${Math.abs(asset.warrantyStatus.daysRemaining)} days ago`}
                    </div>
                  )}
                  <WarrantyDocSection
                    docs={warrantyDocs}
                    canEdit={cap.canEdit}
                    assetId={id}
                    onPreview={setPreviewDoc}
                    onDelete={handleDeleteDoc}
                    onUploaded={refetch}
                  />
                </div>
              )}

              {/* QR Label */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  QR Label
                </h2>
                <QrPreviewButton assetId={id} />
              </div>

              {/* Metadata */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Metadata
                </h2>
                <InfoRow label="Created" value={formatDate(asset.createdAt)} />
                <InfoRow
                  label="Updated"
                  value={formatRelative(asset.updatedAt)}
                />
              </div>
            </div>
          </div>

          {/* Supporting documents — full width */}
          <SupportingDocuments
            docs={supportingDocs}
            canEdit={cap.canEdit}
            assetId={id}
            onPreview={setPreviewDoc}
            onDelete={handleDeleteDoc}
            onUploaded={refetch}
          />
        </div>
      )}

      {tab === "Activity" && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <ActivityLog assetId={id} />
        </div>
      )}

      {tab === "Edit Log" && cap.canViewEditLog && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <EditLog assetId={id} />
        </div>
      )}

      {tab === "Contents" && isStorageUnit && (
        <div className="space-y-5">
          <DriveCapacitySummary asset={asset} />
          <StorageTags tags={asset.tags} />

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Curated Contents
            </h2>
            <StorageContentsTab assetId={id} canEdit={cap.canEdit} />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              File Index
            </h2>
            <FileIndexTab assetId={id} canEdit={cap.canEdit} />
          </div>
        </div>
      )}

      {/* Modals */}
      <ReassignModal
        assetId={id}
        isOpen={modal === "reassign"}
        onClose={() => setModal(null)}
      />
      <ReturnModal
        assetId={id}
        isOpen={modal === "return"}
        onClose={() => setModal(null)}
      />
      <LogCheckModal
        assetId={id}
        isOpen={modal === "check"}
        onClose={() => setModal(null)}
      />
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          isOpen={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}
